/**
 * Privy Expo Auth Provider — React Native implementation of AuthProvider.
 *
 * Uses @privy-io/expo instead of @privy-io/react-auth.
 * Persists the session through getActiveSessionManager() (the RN store on React
 * Native), and mints it via the SIWS wallet-signature path — identical to
 * Phantom/MWA — so no Privy-specific backend verification is required.
 *
 * IMPORTANT — Expo/RN consumers must:
 *   1. Install polyfills: fast-text-encoding, react-native-get-random-values
 *   2. Install @privy-io/expo, @privy-io/expo-native-extensions
 *   3. Call ReactNativeSessionManager.configure({ storage, atob }) at startup
 *   4. Call setPlatform({ ... }) from @bounded-sh/client at startup
 *   5. Wrap their app tree with PrivyProvider from @privy-io/expo
 *   6. Create this provider and pass the Privy hooks/methods via setPrivyMethods()
 *
 * Unlike the web PrivyWalletProvider which renders a hidden React DOM tree
 * to host Privy's React context, this provider expects the RN app itself
 * to render <PrivyProvider> and then bridge the hooks via setPrivyMethods().
 */
import { getActiveSessionManager as SessionManager, createSessionWithSignature, genAuthNonce, genSolanaMessage, } from '@bounded-sh/core';
import { Buffer } from 'buffer';
import { setCurrentUser } from '../../global';
import { getPlatform } from '../../platform';
import { normalizeSolanaRpcUrl, resolveSolanaRpcUrl } from '../solana-rpc';
import { confirmAndCheckTransaction, transactionHasSignature } from './transaction-utils';
import bs58 from 'bs58';
// -----------------------------------------------------------------------
// Provider implementation
// -----------------------------------------------------------------------
export class PrivyExpoProvider {
    constructor(appId, networkUrl = null) {
        this.privyMethods = null;
        // DS3-0411: the embedded Privy wallet address this session was minted for. The
        // Privy account can change under us (a different embedded wallet becomes
        // selected) without a fresh Bounded login; if the currently selected wallet is
        // not the one we authenticated, we must REFUSE to sign rather than sign as a
        // principal the Bounded session never proved. Set on login/restore, cleared on
        // logout; a fresh login rebinds it. Mirrors the injected wallet providers.
        this.authenticatedAddress = null;
        this.appId = appId;
        this.networkUrl = normalizeSolanaRpcUrl(networkUrl);
    }
    /**
     * Bridge Privy hooks from the app's React tree into this provider.
     *
     * Call this from a component rendered inside <PrivyProvider>:
     * ```tsx
     * const privy = usePrivy();
     * const wallet = useEmbeddedSolanaWallet();
     * const { getAccessToken } = usePrivy();
     * const { getIdentityToken } = useIdentityToken();
     *
     * useEffect(() => {
     *   provider.setPrivyMethods({
     *     isReady: privy.isReady,
     *     isAuthenticated: !!privy.user,
     *     user: privy.user,
     *     login: privy.login,
     *     logout: privy.logout,
     *     getAccessToken,
     *     getIdentityToken,
     *     getWalletProvider: async () => {
     *       if (!wallet.wallets[0]) return null;
     *       const provider = await wallet.wallets[0].getProvider();
     *       return {
     *         address: wallet.wallets[0].address,
     *         signMessage: provider.signMessage,
     *         signTransaction: provider.signTransaction,
     *         signAndSendTransaction: provider.signAndSendTransaction,
     *       };
     *     },
     *   });
     * }, [privy.isReady, privy.user, wallet.wallets]);
     * ```
     */
    setPrivyMethods(methods) {
        this.privyMethods = methods;
    }
    // -------------------------------------------------------------------
    // AuthProvider interface
    // -------------------------------------------------------------------
    async login() {
        var _a;
        await this.ensureReady();
        // Check for existing session — verify the stored address matches the
        // currently authenticated Privy wallet (guards against account switches
        // without an explicit logout).
        const session = await SessionManager().getSession();
        if (session && ((_a = this.privyMethods) === null || _a === void 0 ? void 0 : _a.isAuthenticated)) {
            const wallet = await this.privyMethods.getWalletProvider();
            if (wallet && wallet.address === session.address) {
                this.authenticatedAddress = session.address;
                return { provider: this, address: session.address };
            }
            // Stale session — clear and continue to fresh login flow
            await SessionManager().clearSession();
        }
        // Trigger Privy login
        const privyUser = await this.privyMethods.login();
        if (!privyUser)
            return null;
        // Get the wallet
        const wallet = await this.privyMethods.getWalletProvider();
        if (!wallet) {
            throw new Error('No embedded Solana wallet found after login. Ensure embeddedWallets.solana.createOnLogin is configured.');
        }
        // Mint a Bounded session by having the Privy Solana wallet sign the
        // standard SIWS challenge — identical to Phantom/MWA, so the backend mints
        // the session via the proven wallet-signature path (no Privy-specific
        // token verification needed).
        await this.createSession(wallet.address, wallet);
        this.authenticatedAddress = wallet.address;
        const user = { provider: this, address: wallet.address };
        setCurrentUser(user);
        return user;
    }
    async signMessage(message) {
        await this.ensureReady();
        const wallet = await this.getWalletOrThrow('signing');
        const messageBytes = getPlatform().textEncode(message);
        const result = await wallet.signMessage(messageBytes);
        return bs58.encode(result.signature);
    }
    async signTransaction(transaction) {
        await this.ensureReady();
        const { Connection, PublicKey, Transaction, VersionedTransaction } = await import("@solana/web3.js");
        const wallet = await this.getWalletOrThrow('signing this transaction');
        // Ensure blockhash is set
        const isLegacyTx = 'recentBlockhash' in transaction &&
            !('message' in transaction && 'staticAccountKeys' in transaction.message);
        if (isLegacyTx) {
            const legacyTx = transaction;
            if (!legacyTx.recentBlockhash) {
                const rpcUrl = this.getRpcUrl();
                const connection = new Connection(rpcUrl, 'confirmed');
                const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
                legacyTx.recentBlockhash = blockhash;
                legacyTx.lastValidBlockHeight = lastValidBlockHeight;
            }
            if (!legacyTx.feePayer) {
                legacyTx.feePayer = new PublicKey(wallet.address);
            }
        }
        else {
            const versionedTx = transaction;
            if (!versionedTx.message.recentBlockhash) {
                const rpcUrl = this.getRpcUrl();
                const connection = new Connection(rpcUrl, 'confirmed');
                const { blockhash } = await connection.getLatestBlockhash('confirmed');
                versionedTx.message.recentBlockhash = blockhash;
            }
        }
        const result = await wallet.signTransaction(transaction);
        // Deserialize the signed transaction
        const signedBytes = result.signedTransaction;
        try {
            return VersionedTransaction.deserialize(signedBytes);
        }
        catch (_a) {
            return Transaction.from(signedBytes);
        }
    }
    async signAndSubmitTransaction(transaction, feePayer) {
        await this.ensureReady();
        const { Connection, PublicKey } = await import("@solana/web3.js");
        const wallet = await this.getWalletOrThrow('submitting this transaction');
        const rpcUrl = this.getRpcUrl();
        const connection = new Connection(rpcUrl, 'confirmed');
        // Ensure blockhash.
        // DS3-0414: a Solana signature covers the compiled message (recentBlockhash
        // included), so refreshing the blockhash after any signer (sponsor / co-signer)
        // has signed silently invalidates their signature. Only refresh while the
        // transaction is still unsigned; once a real signature is present, submit the
        // caller's transaction verbatim.
        if (!transactionHasSignature(transaction)) {
            const isLegacyTx = 'recentBlockhash' in transaction &&
                !('message' in transaction && 'staticAccountKeys' in transaction.message);
            if (isLegacyTx) {
                const legacyTx = transaction;
                const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
                legacyTx.recentBlockhash = blockhash;
                legacyTx.lastValidBlockHeight = lastValidBlockHeight;
                legacyTx.feePayer = feePayer || new PublicKey(wallet.address);
            }
            else {
                const versionedTx = transaction;
                const { blockhash } = await connection.getLatestBlockhash('confirmed');
                versionedTx.message.recentBlockhash = blockhash;
            }
        }
        const { signature } = await this.signAndSubmitInternal(transaction, wallet, connection);
        return signature;
    }
    async restoreSession() {
        const session = await SessionManager().getSession();
        if (session) {
            this.authenticatedAddress = session.address;
            return { provider: this, address: session.address };
        }
        return null;
    }
    async logout() {
        var _a;
        this.authenticatedAddress = null;
        if ((_a = this.privyMethods) === null || _a === void 0 ? void 0 : _a.isAuthenticated) {
            await this.privyMethods.logout();
        }
        await SessionManager().clearSession();
    }
    async getNativeMethods() {
        return this.privyMethods;
    }
    // -------------------------------------------------------------------
    // Private helpers
    // -------------------------------------------------------------------
    /**
     * Mint a Bounded session from a Privy Solana wallet by signing the canonical
     * SIWS challenge (nonce -> genSolanaMessage -> wallet.signMessage). This reuses
     * createSessionWithSignature — the exact path Phantom, the mobile wallet
     * adapter, and the guest device-key use — so the bounded /session issuer mints
     * the session with no Privy-specific verification (it arrives as a Solana
     * wallet signature, defaulting to authMethod 'phantom' server-side).
     */
    async createSession(address, wallet) {
        const nonce = await genAuthNonce();
        const message = await genSolanaMessage(address, nonce);
        const messageBytes = getPlatform().textEncode(message);
        const signResult = await wallet.signMessage(messageBytes);
        // createSessionWithSignature expects a base64-encoded detached signature
        // (matches the Phantom + guest providers).
        const signature = Buffer.from(signResult.signature).toString('base64');
        const result = await createSessionWithSignature(address, message, signature);
        if (result) {
            await SessionManager().storeSession(address, result.accessToken, result.idToken, result.refreshToken);
        }
    }
    async getWalletOrThrow(action) {
        var _a;
        const wallet = await ((_a = this.privyMethods) === null || _a === void 0 ? void 0 : _a.getWalletProvider());
        if (!wallet) {
            throw new Error('No Privy wallet available. Ensure the user is logged in and an embedded Solana wallet exists.');
        }
        this.assertAuthenticatedAccount(wallet.address, action);
        return wallet;
    }
    /**
     * DS3-0411: refuse to sign when the currently selected Privy embedded wallet
     * differs from the account this Bounded session was authenticated with. Signing
     * under a switched account would produce a message/transaction from an identity
     * the session never proved (requests still carry account A's bearer while the
     * signature/fee-payer come from account B). Require a fresh login to rebind.
     */
    assertAuthenticatedAccount(currentAddress, action) {
        var _a;
        if (!this.authenticatedAddress || currentAddress !== this.authenticatedAddress) {
            throw new Error(`The Privy wallet account changed after login (session is for ` +
                `${(_a = this.authenticatedAddress) !== null && _a !== void 0 ? _a : 'no account'}, wallet is on ${currentAddress}). ` +
                `Sign in again before ${action}.`);
        }
    }
    async signAndSubmitInternal(transaction, wallet, connection) {
        // Use Privy's combined sign+send
        const result = await wallet.signAndSendTransaction(transaction, connection);
        const signature = result.signature;
        const txInfo = await confirmAndCheckTransaction(connection, signature);
        return { signature, txInfo };
    }
    getRpcUrl(network) {
        return resolveSolanaRpcUrl(this.networkUrl, network, "Privy Expo Solana transaction");
    }
    async ensureReady(timeoutMs = 15000) {
        if (!this.privyMethods) {
            throw new Error('PrivyExpoProvider.setPrivyMethods() must be called before using auth. ' +
                'Bridge the Privy hooks from your React Native component tree.');
        }
        if (this.privyMethods.isReady)
            return;
        return new Promise((resolve, reject) => {
            const startTime = Date.now();
            const check = () => {
                var _a;
                if ((_a = this.privyMethods) === null || _a === void 0 ? void 0 : _a.isReady) {
                    resolve();
                }
                else if (Date.now() - startTime > timeoutMs) {
                    reject(new Error('Privy Expo SDK failed to initialize within timeout.'));
                }
                else {
                    setTimeout(check, 100);
                }
            };
            check();
        });
    }
}
